// ============================================================================
// VOTING QUORUM TAB — WorkItemDetailModal 'voting_quorum' tab. Quorum posture
// for the session: required threshold, eligible roll, checked-in/turnout count,
// met/below state, and which agenda items carry a quorum requirement.
// ============================================================================

'use client';

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/total-operations/workflows/digital-voting/quorum-and-results.mdx
 * (published at user-docs.totalaccess.co.za/total-operations/workflows/digital-voting/quorum-and-results)
 */

import React from 'react';
import { CheckCircle2, Gavel, Loader2, Scale, Users } from 'lucide-react';
import type { WorkItem } from '@/components/UniversalWorkItems';
import {
  Badge,
  ITEM_TYPE_LABELS,
  OutcomeBadge,
} from '@/app/client-zone/modules/total-operations/components/voting/shared';
import UserDocsLink from '@/components/shared/UserDocsLink';
import { useVotingSessionDetail } from './useVotingSessionDetail';

interface Props {
  workItem: WorkItem;
  isDark?: boolean;
}

export default function VotingQuorumTab({ workItem, isDark = false }: Props) {
  const { detail, loading, error } = useVotingSessionDetail(String(workItem.id));

  if (loading && !detail) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-400">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
        <Gavel className={`h-10 w-10 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
        <p className="text-sm text-red-600">{error || 'Session not found'}</p>
      </div>
    );
  }

  const { session, quorum, items } = detail;
  const turnoutLabel = session.delivery_mode === 'async' ? 'Turnout' : 'Present';
  const quorumItems = items.filter((i) => i.threshold?.requires_quorum);

  return (
    <div className="space-y-4 p-4">
      {/* Quorum posture */}
      <div className={`rounded-xl border p-4 ${isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-white'}`}>
        <div className="flex items-center justify-between">
          <h3 className={`flex items-center gap-2 text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
            <Scale className="h-4 w-4 text-blue-500" /> Quorum
            <UserDocsLink docsPath="/total-operations/workflows/digital-voting/quorum-and-results#how-quorum-works" label="User guide: Quorum and results" />
          </h3>
          {quorum?.met != null ? (
            <Badge
              label={quorum.met ? 'Quorum met' : 'Below quorum'}
              color={quorum.met ? '#166534' : '#b45309'}
              bg={quorum.met ? '#dcfce7' : '#fef3c7'}
            />
          ) : (
            <Badge
              label={session.quorum_required ? 'Quorum required' : 'No quorum required'}
              color={session.quorum_required ? '#1d4ed8' : '#6b7280'}
              bg={session.quorum_required ? '#dbeafe' : '#f3f4f6'}
            />
          )}
        </div>

        {session.quorum_required ? (
          <div className="mt-3 grid grid-cols-3 gap-3">
            <div className={`rounded-lg p-3 ${isDark ? 'bg-gray-900/60' : 'bg-gray-50'}`}>
              <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Required</p>
              <p className={`mt-0.5 text-lg font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                {session.quorum_percent ?? quorum?.requiredPct ?? '—'}%
              </p>
            </div>
            <div className={`rounded-lg p-3 ${isDark ? 'bg-gray-900/60' : 'bg-gray-50'}`}>
              <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Eligible voters</p>
              <p className={`mt-0.5 text-lg font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                {quorum?.eligible ?? '—'}
              </p>
            </div>
            <div className={`rounded-lg p-3 ${isDark ? 'bg-gray-900/60' : 'bg-gray-50'}`}>
              <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{turnoutLabel}</p>
              <p className={`mt-0.5 text-lg font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                {quorum?.present ?? '—'}
              </p>
            </div>
          </div>
        ) : (
          <p className={`mt-2 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            This session does not require a quorum — outcomes are tallied on votes cast.
          </p>
        )}

        {session.quorum_required && quorum && quorum.eligible > 0 && (
          <div className={`mt-3 rounded-lg border px-3 py-2 text-xs ${isDark ? 'border-gray-700 text-gray-300' : 'border-blue-100 bg-blue-50 text-blue-800'}`}>
            <Users className="mr-1 inline h-3.5 w-3.5" />
            {turnoutLabel} {quorum.present} of {quorum.eligible} eligible voters
            {quorum.requiredPct != null && quorum.requiredPct > 0 && (
              <> — {Math.ceil((quorum.requiredPct / 100) * quorum.eligible)} needed for quorum</>
            )}
          </div>
        )}
      </div>

      {/* Items carrying a quorum requirement */}
      <div className={`rounded-xl border p-4 ${isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-white'}`}>
        <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
          Items requiring quorum
        </h3>
        {quorumItems.length === 0 ? (
          <p className={`mt-2 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            No agenda items carry a quorum requirement — all proceed regardless of turnout.
          </p>
        ) : (
          <ul className="mt-2 space-y-1.5">
            {quorumItems.map((item) => (
              <li
                key={item.id}
                className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-sm ${
                  isDark ? 'border-gray-700 text-gray-200' : 'border-gray-200 text-gray-700'
                }`}
              >
                <span className="flex min-w-0 items-center gap-2">
                  <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-blue-500" />
                  <span className="truncate">{item.title}</span>
                  <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>
                    {ITEM_TYPE_LABELS[item.item_type] ?? item.item_type}
                  </span>
                </span>
                <OutcomeBadge outcome={item.outcome} />
              </li>
            ))}
          </ul>
        )}
        {items.length > 0 && quorumItems.length < items.length && (
          <p className={`mt-2 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            {items.length - quorumItems.length} item{items.length - quorumItems.length === 1 ? '' : 's'} proceed regardless of turnout.
          </p>
        )}
      </div>
    </div>
  );
}
